/**
 * Background Context Menu Component
 *
 * Context menu for empty space in workspace - provides quick actions for creating new items.
 */

import { Table, Link2, FolderPlus, CheckSquare } from 'lucide-react';
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
} from '@loader/ui';

import { modKey } from '@/lib/platform';

// ============================================================================
// TYPES
// ============================================================================

export interface BackgroundContextMenuProps {
  /** Whether user can edit (editor/owner) - viewers cannot create items */
  canEdit?: boolean;
  onCreateTable?: () => void;
  onCreateLinkedTable?: () => void;
  onCreateFolder?: () => void;
  onSelectAll?: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Context menu for workspace background (empty space)
 */
export function BackgroundContextMenu({
  canEdit = true,
  onCreateTable,
  onCreateLinkedTable,
  onCreateFolder,
  onSelectAll,
}: BackgroundContextMenuProps) {
  return (
    <ContextMenuContent>
      {/* Create actions */}
      <span className={!canEdit ? 'cursor-not-allowed' : ''}>
        <ContextMenuItem
          disabled={!canEdit}
          onClick={canEdit ? onCreateTable : undefined}
        >
          <Table className="mr-2 size-4" />
          New Table
        </ContextMenuItem>
      </span>
      <span className={!canEdit ? 'cursor-not-allowed' : ''}>
        <ContextMenuItem
          disabled={!canEdit}
          onClick={canEdit ? onCreateLinkedTable : undefined}
        >
          <Link2 className="mr-2 size-4" />
          New Linked Table
        </ContextMenuItem>
      </span>
      <span className={!canEdit ? 'cursor-not-allowed' : ''}>
        <ContextMenuItem
          disabled={!canEdit}
          onClick={canEdit ? onCreateFolder : undefined}
        >
          <FolderPlus className="mr-2 size-4 fill-amber-500 text-amber-600" />
          New Folder
        </ContextMenuItem>
      </span>

      <ContextMenuSeparator />

      {/* Select all action */}
      <ContextMenuItem onClick={onSelectAll}>
        <CheckSquare className="mr-2 size-4" />
        Select All
        <ContextMenuShortcut>{modKey}A</ContextMenuShortcut>
      </ContextMenuItem>
    </ContextMenuContent>
  );
}
